import { computed, h, nextTick, reactive, ref } from "vue";
import { editRecId, select as selectForm } from "@/core/db";
import Select from '@/components/Select.vue';
import DBStoreRecord from "@/core/db_store_record";
import { genGUID, isAsync, textareaInsertText } from "@/helpers/utils";
import { openPanel } from "@/layouts/layouts";
import { getModel } from "@/tables";
import { list } from "@/api/templates";
import { signalsApi } from "@/api/signals";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import SignalDialog from "@/components/Signals/SignalDialog.vue";
import Bus from "@/core/bus";

export default class DBEditController {
	public el: any;
	public dropDown: any;
	public store: DBStoreRecord;
	public data: any;
	public form: any;

	public structure: any;
	public structureField: any;
	public field: any;

	public label: string;
	public labelId: string;
	public readonly: any;
	public access: boolean;

	public required: any;
	public verified: any;
	public valid: any;
	public feedback: any;
	public message: any;

	public typeahead: any;
	public select: any;
	public selectMode: any;
	public multiSelect: any;
	public msData: any;

	public showPopup: any;
	public config: any;

	public menuShow: any;
	public menuItems: any;

	constructor(public props: any, public searchText: any) {
		this.el = ref(null);
		this.dropDown = ref(null);

		this.verified = ref<boolean>(false);
		this.valid = ref<boolean>(false);
		this.feedback = ref<string>('');
		this.message = ref<string>('');
		this.required = ref<boolean>(false);

		this.form = props.form;
		this.store = props.form.store;
		this.data = this.store.data;

		this.access = false;
		this.label = '';
		this.labelId = '';

		this.menuShow = ref<boolean>(false);
		this.menuItems = ref([]);
		this.msData = reactive({});

		this.showPopup = ref<boolean>(false);

		this.field = ref(this.props.field);

		this.labelId = this.props.id ? this.props.id : `${this.props.field}_` + genGUID();

		this.init();

		this.readonly = computed(
			() => {
				if (this.access) {
					return typeof props.readonly == 'boolean'
						? props.readonly
						: props.form.store.state.readonly || props.form.readonly.value || this.field.value == props.form.store.model.key || this.structure.sql
				} else {
					return true;
				}
			}
		)
	}

	getFieldModel(fields: any, structure: any): any {
		const field: any = fields.shift();

		if (fields.length > 0) {
			return this.getFieldModel(fields, structure.fields[field].model);
		} else {
			return structure.fields[field];
		}
	}

	get valueField() {
		return this.store.getValue(this.props.field);
	}

	get value() {
		return this.store.getValue(this.field.value);
	}

	set value(value: any) {
		this.store.setValue(this.field.value, value);
		this.required.value = this.isRequired() && !value;
	}

	getData(fields: any, data: any): any {
		const field: any = fields.shift();

		if (fields.length > 0) {
			if (!data[field]) data[field] = {};

			return this.getData(fields, data[field]);
		} else {
			return data;
		}
	}

	get notionValue() {
		const fields = this.field.value.split('.');

		const field = fields[fields.length - 1];

		const data = this.getData(fields, this.data);

		return data[`_${field}`];
	}

	get notion() {
		if (this.props.multiSelect) {
			const notion = this.structure?.config?.notion;
			if (notion && typeof notion == 'function') {
				return notion(this.msData, this);
			} else {
				return Object.keys(this.msData).map((el) => this.msData[el]).join(', ');
			}
		} else {
			return this.notionValue;
		}
	}

	set notion(value: any) {
		const fields = this.field.value.split('.');

		const field = fields[fields.length - 1];

		const data = this.getData(fields, this.data);

		data[`_${field}`] = value;
	}

	async addReference() {
		if (typeof this.structure.type == 'object' && this.structure.type.reference) {
			const store = new DBStoreRecord(this.structure.type.reference);

			const data = await store.newRecord(this.structure.config.edit, { [store.model.notion]: this.searchText.value });

			if (data) {
				this.setData(data);

				this.setFocusInput();

				this.verified.value && this.validation();
			}
		}
	}

	onBlur() {
		if (typeof this.structure.type == 'object') {
			if (this.structure.type.reference && this.typeahead) {
				if (!this.value && this.notion) {
					delete this.data[`_${this.field.value}`];
				}
			}
		} else if (typeof this.structure.type == 'string') {
			switch (this.structure.type) {
				case 'DECIMAL': {
					// Для DECIMAL обрабатываем значение с поддержкой точки и запятой
					if (this.value !== null && this.value !== undefined && this.value !== '') {
						// Заменяем запятую на точку
						const normalizedValue = String(this.value).replace(',', '.');
						const numValue = Number(normalizedValue);
						
						if (!isNaN(numValue)) {
							// Не округляем, сохраняем как число (или с указанной точностью, если задана)
							const precision = this.props?.precision || this.config?.precision;
							if (precision != null && precision >= 0) {
								// Если задана точность, форматируем с этой точностью
								this.value = parseFloat(numValue.toFixed(precision));
							} else {
								// Без точности сохраняем как есть, без округления
								this.value = numValue;
							}
						} else {
							this.value = null;
						}
					} else {
						this.value = null;
					}
				}
					break;

				case 'SMALLINT':
				case 'INTEGER':
				case 'BIGINT': {
					// Убеждаемся, что значение является числом
					if (this.value !== null && this.value !== undefined && this.value !== '') {
						const numValue = Number(this.value);
						if (!isNaN(numValue)) {
							this.value = numValue;
						} else {
							this.value = null;
						}
					}

					if (this.props.min != null && this.value < this.props.min) {
						this.value = this.props.min;
					}

					if (this.props.max != null && this.value > this.props.max) {
						this.value = this.props.max;
					}
				}

					break;

				default:
					break;
			}
		}

		this.config?.onBlur && this.config.onBlur(this.value, this.data);
		
		// Вызываем onChange после onBlur для DECIMAL полей, чтобы пересчет происходил после завершения ввода
		if (this.structure.type === 'DECIMAL' && this.config?.onChange) {
			const numValue = typeof this.value === 'string' ? parseFloat(this.value.replace(',', '.')) : Number(this.value);
			if (!isNaN(numValue)) {
				this.config.onChange(numValue, this.data);
			} else {
				this.config.onChange(null, this.data);
			}
		}

		return this.verified.value && !this.valid.value && this.validation();
	}

	onFocusOpenPopup() {
		if (this.structure?.type?.reference && this.props.selectMode && !this.value) {
			this.showPopup.value = true;
		}
	}

	openPopup() {
		if (this.readonly.value) return;

		if (this.showPopup.value) {
			this.closePopup();
		} else {
			this.showPopup.value = true;
		}
	}

	/**
	 * Закрытие выпадающего списка
	 */
	closePopup() {
		this.showPopup.value = false;
	}

	/**
	 * Метод валидации
	 * @returns 
	 */
	validation() {
		if (this.props.validation && this.structure.validation) {
			for (const i in this.structure.validation) {
				const result = this.structure.validation[i](this.store.getValue(this.field.value, false), this.data);

				if (result !== false) {
					this.verified.value = true;
					this.valid.value = false;
					this.feedback.value = result;

					return result;
				}
			}
		}

		this.verified.value = false;
		this.valid.value = true;
		this.feedback.value = '';

		return false;
	}

	/**
	 * Событие "Выбор из справочника"
	 */
	async selectReference() {
		if (!this.readonly.value) {
			if (typeof this.structure.type == 'object' && this.structure.type.reference) {
				const _config = this.config?.select ? this.config.select : {};

				if (this.config?.options) {
					Object.assign(_config, this.config.options);

					//Нужно для табличных частей, фильтру передать текущие данные строки
					if (this.data) Object.assign(_config, { data: this.data });
				}

				const reference = this.structure.type.reference;
				const schema = getModel(reference);

				let owner = null;

				if (schema.ownerField) {
					if (this.structure.depends) {
						owner = this.store.getValue(this.structure.depends);
					} else if (this.config?.owner) {
						owner = this.config.owner;
					}

					if (!owner) return;
				}

				if (schema?.access?.read) {
					const selectData = await selectForm(reference, this.value, owner, _config);

					this.setSelectData(selectData);

					nextTick(() => this.el.value && this.el.value.focus());
				}
			}
		}
	}

	setSelectData(data: any) {
		const reference = this.structure.type.reference;
		const schema = getModel(reference);

		if (data && data[schema.key] != this.value) {
			this.value = data[schema.key];
			this.notion = data[schema.notion];

			this.store.clearDepends(this.field.value);

			if (this.config?.onSelect) this.config.onSelect(data, this.store);

			this.verified.value && !this.valid.value && this.validation();
		}
	}

	/**
	 * Открытие формы редактирования справочника
	 */
	async openReference() {
		if (typeof this.structure.type == 'object' && this.structure.type.reference && this.value != '00000000-0000-0000-0000-000000000000') {
			const schema = getModel(this.structure.type.reference);
			if (schema.access?.read) {
				let owner = null;

				if (schema.ownerField) {
					if (this.structure.depends) {
						owner = this.store.getValue(this.structure.depends);
					} else if (this.config?.owner) {
						owner = this.config.owner;
					}

					if (!owner) return;
				}

				await editRecId(
					this.structure.type.reference,
					this.value,
					owner,
					this.config?.edit ? this.config.edit : {}
				);

				nextTick(() => this.el.value && this.el.value.focus());
			}
		}
	}

	setFocusInput() {
		const input = this.el.value;
		if (this.props.mask) {
			input.$el.focus();
		} else {
			if (input) input.focus()
		}
	}

	init() {
		this.field.value = this.props.field;

		const field = this.field;

		this.structure = this.getFieldModel(field.value.split('.'), this.store.state);

		if (this.structure) {
			this.label = typeof this.props.label == 'boolean' ? this.structure.description : this.props.label;

			this.structureField = this.structure.type?.fields ? this.getFieldModel(field.value.split('.'), this.store.state) : null;

			if (this.structureField) {
				if (this.value) {
					field.value = this.value;

					this.structure = this.getFieldModel(field.value.split('.'), this.store.state);
				}

				if (typeof this.structureField?.access == 'boolean') {
					this.access = this.structureField.access;
				} else {
					this.access = this.structureField?.access ? this.store.availabeRole(this.structureField.access, field.value, this.structureField, this.data) : true;
				}
			} else {
				if (typeof this.structure.access == 'boolean') {
					this.access = this.structure.access;
				} else {
					this.access = this.structure.access ? this.store.availabeRole(this.structure.access, field.value, this.structure, this.data) : true;
				}
			}

			this.config = this.structure?.config ? this.structure.config : {};

			this.typeahead = this.config?.typeahead;
			this.select = this.config?.select;

			this.selectMode = this.props.selectMode || (this.structure.type?.enum && !this.config?.radio);

			this.multiSelect = this.props.multiSelect;

			if (this.multiSelect && this.value) this.msData[this.value] = this.notionValue;
		}

		setTimeout(() => this.showPopup.value = false, 0);
	}

	clearValue() {
		this.field.value = this.props.field;

		if (this.structureField) {
			for (const field of this.structureField.type.fields) {
				this.store.clearField(field.field);
			}

			const fields = this.field.value.split('.');

			const field = fields[fields.length - 1];

			const data = this.getData(fields, this.data);

			data[field] = null;
		} else {
			this.store.clearField(this.field.value);
		}

		this.init();

		if (this.config?.onSelect) this.config?.onSelect(null, this.form.store);
		if (this.config?.onClear) this.config?.onClear(this.form.store);

		this.setFocusInput();

		if (this.multiSelect) {
			Object.keys(this.msData).forEach((key: any) => delete this.msData[key]);

			if (this.config?.onMultiSelect) this.config.onMultiSelect(this.msData, this);
		}
	}

	selectType() {
		openPanel({
			modal: true,
			caption: this.structureField?.config?.description ? this.structureField.config.description : 'Выбор типа поля',
			onCreate: (panelModal: any) => {
				return {
					component: h(Select, {
						field: 'description',
						items: this.structureField.type.fields,
						onSelect: (data: any) => {
							this.value = data.field;

							this.init();

							panelModal.close();

							if (this.structure.type.reference) {
								this.selectReference();
							} else {
								nextTick(() => this.el.value && this.el.value.focus());
							}
						}
					}),
					buttons: [
						{
							class: "btn btn-action",
							caption: 'Выбрать',
							onClick: async () => panelModal.ref.select()
						}
					]
				}
			}
		})
	}

	isRequired() {
		return this.props.validation && this.structure.validation && Object.keys(this.structure.validation).includes('isRequired');
	}

	/**
	 * Установка значения
	 * @param value 
	 */
	setValue(value: any) {
		this.value = value;

		this.validation();
	}

	async openMenu(menuItems: any) {
		setTimeout(() => this.menuShow.value = true, 0);

		const items: any = this.menuItems.value;

		items.splice(0, items.length);

		const _menuItems = typeof menuItems == 'function' ? (isAsync(menuItems) ? await menuItems(this) : menuItems(items)) : menuItems;

		_menuItems.forEach((el: any) => items.push(el))
	}

	/**
	 * Открытие меню textarea
	 * @param event 
	 */
	async onOpenMenuTextarea() {
		const items = await this.createTemplatesMenu();

		this.openMenu(items);
	}

	setTemplateValue(value: any) {

		if (typeof this.structure.type == 'string') {
			if (this.config?.onTemplate) this.config.onTemplate(value, this.store);

			if (this.structure.type == 'TEXT') {
				textareaInsertText(this.el.value, value.template);

				this.setValue(this.el.value.value);

				nextTick(() => this.updateTextareaHeight());
			} else {
				this.setValue(value.template);
			}
		}
	}

	async templates() {
		const result = await selectForm(
			'templates',
			'',
			null,
			{
				default: {
					document_type: this.store.model.id,
					field: this.field.value,
					template: this.el.value.value
				},
				filters: [
					{
						where: '"templates"."document_type"=:document_type and "templates"."field"=:field',
						params: {
							field: this.field.value,
							document_type: this.store.model.id
						}
					}
				]
			}
		)

		if (result) this.setTemplateValue(result);
	}

	async createTemplatesMenu() {
		const result: any = [
			{
				icon: <i class="icon icon-file"> </i>,
				caption: 'Шаблоны',
				onClick: async () => await this.templates()
			}
		];

		const items: any = [];

		const templates = this.structure?.config?.templates;

		if (templates) {
			const itemsTemplates = typeof templates == 'function' ? templates(this) : templates;
			for (const item of itemsTemplates) {
				const template: any = {
					icon: <i class="icon icon-file"> </i>,
					caption: item.template,
					title: item.template,
					onClick: async () => {
						// При выборе родительского шаблона передаем весь объект с items
						this.setTemplateValue(item.items ? { ...item } : item);
					}
				}

				if (item.items) {
					template.items = item.items.map((el: any) => ({
						icon: <i class="icon icon-file"> </i>,
						caption: el.template,
						title: el.template,
						onClick: async () => this.setTemplateValue(el)
					}))
				}

				items.push(template);
			}
		}

		const response: any = await list(this.store.model.id, this.field.value);

		if (response.length > 0) {
			for (const template of response) {
				if (template.items.length > 0) {
					items.push({
						icon: <i class="icon icon-file"> </i>,
						caption: template.template,
						title: template.template,
						onClick: async () => {
							// При выборе родительского шаблона передаем весь объект с items
							this.setTemplateValue({ ...template, items: template.items });
						},
						items: template.items.map((el: any) => ({
							icon: <i class="icon icon-file"> </i>,
							caption: el.template,
							title: el.template,
							onClick: async () => this.setTemplateValue(el)
						}))
					})
				} else {
					items.push({
						icon: <i class="icon icon-file"> </i>,
						caption: template.template,
						title: template.template,
						onClick: async () => this.setTemplateValue(template)
					})
				}
			}
		}

		if (items.length > 0) {
			result.push({ caption: '-' });

			items.forEach((item: any) => result.push(item));
		}

		return result;
	}

	updateTextareaHeight() {
		if (typeof this.structure.type === 'string') {
			if (this.structure.type == 'TEXT' && this.props.rows > 0) {
				const textarea = this.el.value;

				if (textarea) {
					textarea.style['height'] = 'auto';
					if (textarea.scrollHeight > 0) {
						textarea.style['height'] = (textarea.scrollHeight + 2) + 'px';
					}
				}
			}
		}
	}

	triggers() {
		const result = [];

		if (typeof this.structure.type == 'object') {
			if (this.structure.type.reference) {
				if (this.readonly.value) {
					const schema = getModel(this.structure.type.reference);
					if (schema?.access?.read && this.props.view) {
						result.push({
							text: <i class="icon icon-eye"></i>,
							title: 'Просмотр (F2)',
							onClick: async () => await this.openReference()
						});
					}
				} else {
					if (this.props.selectMode || this.props.multiSelect) {
						result.push({
							text: <i class="icon icon-x"></i>,
							title: 'Очистить (F8)',
							onClick: () => this.clearValue()
						});
					} else {
						result.push({
							text: <i class="icon icon-more-horizontal"></i>,
							title: 'Выбрать (F4)',
							onClick: async () => await this.selectReference()
						});

						const schema = getModel(this.structure.type.reference);
						if (schema?.access?.update) {
							result.push({
								text: <i class="icon icon-edit"></i>,
								title: 'Изменить (F2)',
								onClick: async () => await this.openReference()
							});
						} else {
							if (this.props.view) {
								result.push({
									text: <i class="icon icon-eye"></i>,
									title: 'Просмотр (F2)',
									onClick: async () => await this.openReference()
								});
							}
						}

						result.push({
							text: <i class="icon icon-x"></i>,
							title: 'Очистить (F8)',
							onClick: () => this.clearValue()
						});
					}
				}
			} else if (this.structure.type.enum) {
				if (this.props.clear && !this.readonly.value && !this.props.radio) {
					result.push({
						text: <i class="icon icon-x"></i>,
						title: 'Очистить (F8)',
						onClick: () => this.clearValue()
					});
				}
			} else if (!this.readonly.value && this.structure.type.fields) {
				result.push({
					text: <i class="icon icon-more-horizontal"></i>,
					title: 'Выбор типа',
					onClick: () => this.selectType()
				});
			}
		} else if (typeof this.structure.type == 'string') {
			if (this.props.clear && !this.readonly.value && this.structure.type != 'BOOLEAN') {
				result.push({
					text: <i class="icon icon-x"></i>,
					title: 'Очистить (F8)',
					onClick: () => this.clearValue()
				});
			}
		}

		if (this.structure?.config?.triggers) {
			const triggers = typeof this.structure.config.triggers == 'function' ? this.structure.config.triggers(this) : this.structure.config.triggers;

			if (triggers.length > 0) triggers.forEach((el: any) => { result.push(el) })
		}

		// Добавляем кнопку уведомлений, если поле уведомляемое
		if (this.isNotifiable() && !this.readonly.value) {
			result.push({
				text: <i class="icon icon-arrow-forward-outline"></i>,
				title: 'Уведомления',
				menu: async () => await this.createSignalsMenu()
			});
		}

		return result;
	}

	/**
	 * Проверяет, может ли поле быть уведомляемым
	 */
	isNotifiable() {
		return this.structure?.config?.notifiable === true;
	}

	/**
	 * Получает существующие уведомления для поля
	 */
	async getSignals(): Promise<any[]> {
		if (!this.store.data.id || !this.isNotifiable()) {
			return [];
		}
		try {
			const result = await signalsApi.getByPatientAndField(this.store.data.id, this.field.value);
			return Array.isArray(result) ? result : [];
		} catch (error) {
			console.error('Ошибка при получении уведомлений:', error);
			return [];
		}
	}

	/**
	 * Создает меню для уведомлений
	 */
	async createSignalsMenu() {
		const result: any = [
			{
				icon: <i class="icon icon-arrow-forward-outline"> </i>,
				caption: 'Создать уведомление',
				onClick: async () => await this.openSignalsDialog()
			}
		];

		// Проверяем существующие уведомления
		const existingSignals = await this.getSignals();
		if (existingSignals.length > 0) {
			result.push({ caption: '-' });
			for (const signal of existingSignals) {
				result.push({
					icon: <i class="icon icon-arrow-forward-outline"> </i>,
					caption: `Уведомление на ${dayjs(signal.notify_date).format('DD.MM.YYYY')} (редактировать)`,
					title: signal.description,
					onClick: async () => await this.editSignal(signal)
				});
			}
			result.push({ caption: '-' });
			result.push({
				icon: <i class="icon icon-x"> </i>,
				caption: 'Удалить все уведомления',
				onClick: async () => await this.deleteAllSignals()
			});
		}

		return result;
	}

	/**
	 * Открывает диалог создания/редактирования уведомления
	 */
	async openSignalsDialog(signal?: any) {
		if (!this.store.data.id) {
			Swal.fire({
				title: 'Ошибка',
				text: 'Сначала сохраните запись',
				icon: 'error'
			});
			return;
		}

		const fieldValue = this.value || '';
		if (!fieldValue) {
			Swal.fire({
				title: 'Ошибка',
				text: 'Поле должно иметь значение для создания уведомления',
				icon: 'error'
			});
			return;
		}

		const fieldDescription = this.structure?.description || this.field.value;

		openPanel({
			modal: true,
			caption: signal ? 'Редактировать уведомление' : 'Создать уведомление',
			width: '500px',
			onCreate: (panelModal: any) => {
				return {
					component: h(SignalDialog, {
						fieldValue: fieldValue,
						fieldDescription: fieldDescription,
						signal: signal
					}),
					buttons: [
						{
							class: "btn btn-action",
							caption: signal ? 'Сохранить' : 'Создать',
							onClick: async (panel: any) => {
								const signalData = panel.ref?.store?.data;
								if (!signalData) {
									Swal.fire({
										title: 'Ошибка',
										text: 'Компонент не инициализирован',
										icon: 'error'
									});
									return;
								}

								// Валидация
								if (signalData.signalType === 'custom' && !signalData.customDate) {
									Swal.fire({
										title: 'Ошибка',
										text: 'Выберите дату уведомления',
										icon: 'error'
									});
									return;
								}

								// Вычисляем дату уведомления
								let notifyDate: Date | null = null;
								const fieldDate = new Date(fieldValue);
								if (!isNaN(fieldDate.getTime())) {
									if (signalData.signalType === 'week') {
										notifyDate = dayjs(fieldDate).subtract(1, 'week').toDate();
									} else if (signalData.signalType === 'month') {
										notifyDate = dayjs(fieldDate).subtract(1, 'month').toDate();
									} else if (signalData.signalType === 'custom') {
										notifyDate = new Date(signalData.customDate);
									}
								}

								if (!notifyDate || isNaN(notifyDate.getTime())) {
									Swal.fire({
										title: 'Ошибка',
										text: 'Неверная дата уведомления',
										icon: 'error'
									});
									return;
								}

								try {
									if (signal) {
										// Сохраняем текущее значение is_completed из signal, так как в SignalDialog нет этого поля
										const result = await signalsApi.update(signal.id, notifyDate, signalData.description || '', signal.is_completed || false);
										Swal.fire({
											title: 'Успешно',
											text: 'Уведомление обновлено',
											icon: 'success',
											timer: 2000
										});
										// Генерируем событие для обновления счетчика
										Bus.$emit('save-data', { table: 'signals', data: result || { id: signal.id, is_completed: signal.is_completed }, newrecord: false });
									} else {
										const result = await signalsApi.create(
											this.store.data.id,
											this.store.name,
											this.field.value,
											String(fieldValue),
											notifyDate,
											signalData.description || ''
										);
										Swal.fire({
											title: 'Успешно',
											text: 'Уведомление создано',
											icon: 'success',
											timer: 2000
										});
										// Генерируем событие для обновления счетчика
										Bus.$emit('save-data', { table: 'signals', data: result || {}, newrecord: true });
									}
									panel.close();
								} catch (error) {
									Swal.fire({
										title: 'Ошибка',
										text: 'Не удалось сохранить уведомление',
										icon: 'error'
									});
								}
							},
						},
					],
				};
			},
		});
	}

	/**
	 * Редактирует уведомление
	 */
	async editSignal(signal: any) {
		await this.openSignalsDialog(signal);
	}

	/**
	 * Удаляет все уведомления для поля
	 */
	async deleteAllSignals() {
		const result = await Swal.fire({
			title: 'Удалить все уведомления?',
			text: 'Это действие нельзя отменить',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Удалить',
			cancelButtonText: 'Отмена'
		});

		if (result.isConfirmed) {
			try {
				const signals = await this.getSignals();
				for (const signal of signals) {
					await signalsApi.delete(signal.id);
				}
				Swal.fire({
					title: 'Успешно',
					text: 'Все уведомления удалены',
					icon: 'success',
					timer: 2000
				});
			} catch (error) {
				Swal.fire({
					title: 'Ошибка',
					text: 'Не удалось удалить уведомления',
					icon: 'error'
				});
			}
		}
	}

	/**
	 * Установка значения
	 * @param data 
	 */
	setData(data: any) {
		if (this.typeahead) {
			const reference = this.structure.type?.reference;

			if (reference) {
				const schema = getModel(reference);

				this.value = data[schema.key];
				this.notion = data[schema.notion] ? data[schema.notion] : data[schema.key];
			} else {
				this.value = data[this.props.textField];
			}
		} else if (this.multiSelect) {
			if (data.id in this.msData) {
				delete this.msData[data.id];
			} else {
				this.msData[data.id] = data.name;
			}

			if (this.config?.onMultiSelect) this.config.onMultiSelect(this.msData, this);
		} else {
			this.value = data[this.props.valueField];
			this.notion = data[this.props.textField];
		}

		if (this.config?.onSelect) this.config.onSelect(data, this.store);
		if (this.config?.onChange) this.config.onChange(this.value, this.data);

		this.store.clearDepends(this.field.value);

		this.validation();
	}
}
